/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo } from 'react'
import { Typography } from '@mui/material'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import { getValueByPath } from '@/components/project-settings/params-schema-property-fields/PropertySelectFromForeignKey'
import useForeignKeyDetailEndpoint from '@/components/project-settings/params-schema-property-fields/useForeignKeyDetailEndpoint'
import useForeignKeyListEndpoint from '@/components/project-settings/params-schema-property-fields/useForeignKeyListEndpoint'

type ParamDisplayProps = {
  paramValue: string
  paramSchema: any
}

function getValueName(data: Record<string, any>, path?: string) {
  return path ? getValueByPath(data, path) : data?.name || data?.code || ''
}

function updateRawValueBySchema(paramValue: string, paramSchema: any): string {
  const { code, type, items, foreignKey } = paramSchema

  if (!Array.isArray(paramValue)) {
    if (type === 'boolean') {
      return paramValue ? 'True' : 'False'
    }
    if (code === 'select' && !foreignKey) {
      const optionLabel = paramSchema.options.find(
        (option: { value: string }) => option.value === paramValue,
      )?.label
      return optionLabel || paramValue
    }
    return paramValue
  }

  if (items?.type === 'object') {
    return paramValue.map((v) => v.value).join(', ')
  }

  return paramValue.join(',')
}

function useForeignKeyDisplayValue(updatedValue: string, paramSchema: any) {
  const { foreignKey, type, items, display } = paramSchema

  const shouldFetchForeignKeyData =
    !!updatedValue &&
    !!foreignKey &&
    (paramSchema.format === 'uuid' || items?.format === 'uuid')

  const fieldsFilter = useMemo(() => {
    if (!foreignKey?.listQueryParams) {
      return undefined
    }

    return Object.entries(foreignKey.listQueryParams).reduce<
      Record<string, string>
    >((acc, [key, value]) => {
      if (key.includes('fields__')) {
        acc[key] = (value as Record<string, string>).static_value
      }
      return acc
    }, {})
  }, [foreignKey])

  // Fetch single foreign key detail
  const { data: singleForeignData, isPending: isSingleDataPending } =
    useForeignKeyDetailEndpoint<{ name: string; code: string }>({
      url: foreignKey?.listEndpoint,
      id: updatedValue,
      filters: fieldsFilter,
      enabled: shouldFetchForeignKeyData && type !== 'array',
    })

  // Fetch multiple foreign key details
  const { allData: multipleForeignData, isPending: isMultipleDataPending } =
    useForeignKeyListEndpoint({
      url: foreignKey?.listEndpoint,
      filters: {
        id__in: updatedValue,
        ...fieldsFilter,
      },
      enabled: shouldFetchForeignKeyData && type === 'array',
    })

  const isLoading =
    type === 'array' ? isMultipleDataPending : isSingleDataPending

  // Generate display value from foreign key data
  const foreignKeyDisplayValue = useMemo(() => {
    if (!shouldFetchForeignKeyData || isLoading) {
      return null
    }

    const namePath = display.item?.name_path

    if (singleForeignData) {
      return getValueName(singleForeignData, namePath) || updatedValue
    }

    if (multipleForeignData.length > 0) {
      return (
        <>
          {multipleForeignData.map((data) => (
            <Typography key={data.id} variant="body2" color="text.secondary">
              {getValueName(data, namePath)}
            </Typography>
          ))}
        </>
      )
    }

    return null
  }, [
    shouldFetchForeignKeyData,
    isLoading,
    display.item?.name_path,
    singleForeignData,
    multipleForeignData,
    updatedValue,
  ])

  return {
    foreignKeyDisplayValue,
    isLoading: shouldFetchForeignKeyData && isLoading,
  }
}

function ParamDisplay({ paramValue, paramSchema }: ParamDisplayProps) {
  const { display, is_prime } = paramSchema

  const updatedValue = updateRawValueBySchema(paramValue, paramSchema)

  // Fetch foreign key data if needed
  const { foreignKeyDisplayValue, isLoading } = useForeignKeyDisplayValue(
    updatedValue,
    paramSchema,
  )

  if (isLoading) {
    return null
  }

  const displayValue = foreignKeyDisplayValue || updatedValue

  return (
    <Typography
      variant="body2"
      component="div"
      sx={{
        color: is_prime ? SUPER_USER_ONLY_COLOR : undefined,
        whiteSpace: 'pre-wrap',
      }}
    >
      {display.label && <b>{display.label}:</b>} {displayValue}
    </Typography>
  )
}

export default memo(ParamDisplay)
